/**
 * Stateless HUD building blocks: small DOM factories + lookup tables shared by
 * the Hud controller. Kept out of hud.ts so that file stays focused on the
 * selection state machine and live per-frame updates.
 */
import { icon, RESOURCE_ICON, CLASS_ICON, type IconName } from "./icons";
import type { World } from "../sim/world";
import type { Registry } from "../data/registry";
import type { Eid } from "../sim/types";
import type { Ident } from "../sim/components";

export const WORKER_BUILDABLE = ["house", "field", "barracks", "stables", "temple", "storage_pit", "dock", "tower", "wall", "gate"];
export const RES_ORDER = ["food", "wood", "gold", "stone"] as const;

/** Building id → menu glyph. */
export const BUILDING_ICON: Record<string, IconName> = {
  house: "tent", field: "wheat", barracks: "swords", stables: "warehouse",
  temple: "landmark", storage_pit: "package", dock: "anchor",
  tower: "tower", wall: "wall", gate: "gate",
};
/** Unit class → accent colour for the multi-select breakdown. */
export const CLASS_COLOR: Record<string, string> = {
  worker: "#e07a5f", infantry: "#e07a5f", ranged: "#81b29a",
  cavalry: "#e9c46a", priest: "#a3d45f", siege: "#8d99ae",
  naval: "#4ec0e0",
};

/** A live-updatable bar: the wrapper element, its fill, and the value text node. */
export type BarRef = { el: HTMLElement; fill: HTMLElement; text: HTMLElement };

export function el(tag: string, cls?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

export function hpColor(frac: number): string {
  return frac > 0.5 ? "#5ba12a" : frac > 0.25 ? "#f59e0b" : "#ef4444";
}

export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** A bare bar (fill + centred text), used for the in-slot production countdown. */
export function bar(kind: string): BarRef {
  const wrap = el("div", `bar ${kind}`);
  const fill = el("div", "bar-fill");
  const text = el("span", "bar-text");
  wrap.append(fill, text);
  return { el: wrap, fill, text };
}

/** A labelled bar: a head row (icon + label … value) above a slim track. The
 *  returned `text` is the head's value span, so live updates write there. */
export function labeledBar(kind: string, glyph: IconName, label: string): BarRef {
  const block = el("div", "bar-block");
  const head = el("div", "bar-head",
    `<span class="lbl">${icon(glyph, 13)} ${label}</span><span class="val"></span>`);
  const track = el("div", `bar ${kind}`);
  const fill = el("div", "bar-fill");
  track.appendChild(fill);
  block.append(head, track);
  return { el: block, fill, text: head.querySelector(".val") as HTMLElement };
}

/** A rounded stat chip: icon + value. */
export function chip(name: IconName, value: string, title: string): HTMLElement {
  const c = el("div", "chip", `<i>${icon(name, 15)}</i><b>${value}</b>`);
  c.title = title;
  return c;
}

/** A 76px identity portrait tile, tinted by the owner's colour. */
export function portrait(world: World, eid: Eid, ident: Ident): HTMLElement {
  const color = world.players.get(world.owner.get(eid)?.player ?? -1)?.color ?? "#8a8a8a";
  const glyph: IconName =
    ident.kind === "building" ? "shield" : ident.kind === "resource" ? "package" : CLASS_ICON[ident.cls ?? "worker"] ?? "users";
  const p = el("div", "portrait");
  p.style.borderColor = color;
  p.style.color = color;
  p.innerHTML = icon(glyph, 38);
  return p;
}

/** Cost as small coloured resource chips (icon + amount). */
export function costHtml(reg: Registry, cost: Record<string, number | undefined>): string {
  return Object.entries(cost)
    .filter(([, v]) => v)
    .map(([k, v]) => {
      const color = reg.resource(k)?.color ?? "#a1a1aa";
      return `<span class="c"><i style="color:${color}">${icon(RESOURCE_ICON[k] ?? "package", 11)}</i>${v}</span>`;
    })
    .join("");
}

/** Human-readable current activity of a unit ("Gathering wood", "Idle", …). */
export function unitStatus(world: World, eid: Eid, reg: Registry): string {
  const b = world.builder.get(eid);
  if (b?.foundation != null) return `Building ${world.ident.get(b.foundation)?.name ?? "structure"}`;
  const g = world.gatherer.get(eid);
  const mv = world.move.get(eid);
  const intent = mv?.intent;
  if (g && (intent?.kind === "gather" || g.node != null || g.carrying > 0)) {
    const res = g.resource ?? (g.node != null ? world.resource.get(g.node)?.resource : null);
    const rn = res ? reg.resource(res)?.name ?? res : "resources";
    return g.state === "toDropoff" ? `Returning ${rn}` : `Gathering ${rn}`;
  }
  if (intent?.kind === "attack" || intent?.kind === "attackMove") return "Attacking";
  if (intent?.kind === "build") return "Moving to build";
  if (intent?.kind === "move" || (mv && mv.path.length > 0)) return "Moving";
  return "Idle";
}

/** Multi-select identity: big count + per-class breakdown. */
export function groupIdentity(world: World, units: Eid[]): HTMLElement[] {
  const head = el("div", "id-head",
    `<div class="id-meta"><div class="id-name id-count">${units.length} <em>units selected</em></div>` +
    `<div class="id-sub">selected group</div></div>`);
  const byClass = new Map<string, number>();
  for (const e of units) {
    const cls = world.ident.get(e)?.cls ?? "unit";
    byClass.set(cls, (byClass.get(cls) ?? 0) + 1);
  }
  const list = el("div", "group-breakdown");
  for (const [cls, n] of byClass) {
    const color = CLASS_COLOR[cls] ?? "#a1a1aa";
    list.innerHTML +=
      `<div class="gb-row"><i style="color:${color}">${icon(CLASS_ICON[cls] ?? "users", 14)}</i>` +
      `<b>×${n}</b>${cap(cls)}</div>`;
  }
  return [head, list];
}

/** Multi-select unit grid (icon + HP pip), each cell focuses its unit. */
export function selectionGrid(world: World, units: Eid[], onFocus: (e: Eid) => void): HTMLElement {
  const grid = el("div", "sel-grid");
  for (const e of units) {
    const ident = world.ident.get(e);
    if (!ident) continue;
    const cell = el("button", "grid-cell");
    cell.title = ident.name;
    cell.style.color = world.players.get(world.owner.get(e)?.player ?? -1)?.color ?? "#aaa";
    cell.innerHTML = `${icon(CLASS_ICON[ident.cls ?? "worker"] ?? "users", 21)}<span class="cell-hp"><span class="cell-hp-fill"></span></span>`;
    const hp = world.health.get(e);
    if (hp) {
      const frac = Math.max(0, hp.hp / hp.maxHp);
      const fill = cell.querySelector(".cell-hp-fill") as HTMLElement;
      fill.style.width = frac * 100 + "%";
      fill.style.background = hpColor(frac);
    }
    cell.addEventListener("click", () => onFocus(e));
    grid.appendChild(cell);
  }
  return grid;
}

/** Half-cost refund chips for a cancelled foundation. */
export function refundHtml(reg: Registry, ident: Ident): string {
  if (!ident.civ) return "";
  const cost = reg.building(ident.civ, ident.defId).cost;
  const half: Record<string, number> = {};
  for (const [k, v] of Object.entries(cost)) half[k] = Math.floor((v ?? 0) * 0.5);
  return `refund ${costHtml(reg, half)}`;
}

/** Construction (foundation) panel — identity (hammer portrait + progress) and a
 *  context "site status" (builders, time-left, cancel). Returns the elements to
 *  append plus the live refs the controller updates each frame. */
export function constructionPanel(
  ident: Ident, reg: Registry, onCancel: () => void,
): { identity: HTMLElement[]; context: HTMLElement[]; construction: BarRef; buildersEl: HTMLElement; timeLeftEl: HTMLElement; buildTime: number } {
  const head = el("div", "id-head");
  const p = el("div", "portrait constructing", icon("hammer", 38));
  head.append(p, el("div", "id-meta",
    `<div class="id-name">${ident.name}</div>` +
    `<div class="id-sub constructing"><span class="dot"></span>under construction</div>`));
  const prog = labeledBar("build", "hammer", "build progress");

  const status = el("div", "site-status");
  const bcard = el("div", "stat-card",
    `<div class="sc-h">${icon("users", 13)} builders</div><div class="sc-v"><b>0</b> assigned</div>`);
  const tcard = el("div", "stat-card",
    `<div class="sc-h">${icon("clock", 13)} time left</div><div class="sc-v"><b>—</b></div>`);
  status.append(bcard, tcard);
  const cancel = el("button", "cancel-btn",
    `${icon("x", 16)} Cancel construction <span class="refund">${refundHtml(reg, ident)}</span>`);
  cancel.addEventListener("click", onCancel);

  return {
    identity: [head, prog.el],
    context: [el("div", "ctx-label", `<i>${icon("hammer", 14)}</i> site status`), status, cancel],
    construction: prog,
    buildersEl: bcard.querySelector(".sc-v b") as HTMLElement,
    timeLeftEl: tcard.querySelector(".sc-v b") as HTMLElement,
    buildTime: ident.civ ? reg.building(ident.civ, ident.defId).buildTime : 30,
  };
}
